import { Virtuoso } from "react-virtuoso";

import { areObjectsEqual } from "features/templates/utils";

import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useActiveConversationState from "../../hooks/useActiveConversationState";
import useKeywordsSearch from "../../hooks/useKeywordsSearch";
import ConversationItem from "./ConversationItem";

import type { JSX } from "react";
import type { Conversation } from "../../types";

interface ConversationsListProps {
  conversations: Conversation[] | undefined;
  canFetchNextPage: boolean;
  loadMoreConversations: () => void;
  endComponent: JSX.Element;
}

export default function ConversationsList({
  conversations,
  canFetchNextPage,
  loadMoreConversations,
  endComponent,
}: ConversationsListProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { keywords } = useKeywordsSearch();
  const { activeConversationState, setActiveConversationState } =
    useActiveConversationState();

  return (
    <Virtuoso
      data={conversations}
      endReached={canFetchNextPage ? loadMoreConversations : undefined}
      itemContent={(index, conversation) => {
        let isSelected = false;
        let isNextSelected = false;

        if (activeConversationState) {
          const { conversationUrn, highlight } = activeConversationState;

          isSelected =
            conversation.conversation_urn === conversationUrn &&
            (keywords
              ? conversation.highlight &&
                highlight &&
                areObjectsEqual(conversation.highlight, highlight)
              : true);

          const nextConversation = conversations[index + 1];

          isNextSelected =
            nextConversation &&
            conversationUrn === nextConversation.conversation_urn &&
            (keywords
              ? nextConversation.highlight &&
                highlight &&
                areObjectsEqual(nextConversation.highlight, highlight)
              : true);
        }
        return (
          <ConversationItem
            key={`${conversation.conversation_urn}-${conversation.last_activity_at}`}
            isSelected={isSelected}
            isNextSelected={isNextSelected}
            conversation={conversation}
            onSelect={() => {
              setActiveConversationState(
                conversation.conversation_urn,
                conversation.owner,
                conversation.highlight,
              );
            }}
            isLoading={false}
          />
        );
      }}
      style={{ flex: 1, overflowX: "hidden" }}
      className="scrollbar-thin [mask-image:_linear-gradient(to_bottom,_black_128px,_black_calc(100%-50px),transparent_100%)]"
      useWindowScroll={!isDesktop}
      components={endComponent ? { Footer: () => endComponent } : {}}
      increaseViewportBy={{ bottom: 4000, top: 0 }}
    />
  );
}
